import { Link } from 'react-router-dom';
import { 
  ArrowRight, Database, BarChart3, DollarSign, Network, Zap, 
  Droplets, Flame, Mountain, Sun, Gem, CloudCog,
  Search, Building2, TrendingUp, PiggyBank, Leaf, LineChart,
  Globe2, Coins, Sprout
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, BarChart, Bar, CartesianGrid, Tooltip } from 'recharts';

// Dashboard Interface Visual Component for Hero
const HeroDashboard = () => {
  const areaData = [
    { month: 'J', value: 30 }, { month: 'F', value: 45 }, { month: 'M', value: 38 },
    { month: 'A', value: 52 }, { month: 'M', value: 48 }, { month: 'J', value: 65 },
    { month: 'J', value: 58 }, { month: 'A', value: 72 },
  ];
  
  const barData = [
    { name: 'Q1', value: 85 }, { name: 'Q2', value: 72 }, 
    { name: 'Q3', value: 95 }, { name: 'Q4', value: 88 },
  ];

  return (
    <div className="relative bg-[#0F2A4D] rounded-xl overflow-hidden shadow-2xl border border-[#1F3C88]/30">
      {/* Dashboard Header */}
      <div className="bg-[#0a1e38] px-6 py-4 border-b border-[#1F3C88]/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#007BFF] to-[#0056b3] flex items-center justify-center shadow-lg">
              <span className="text-white font-bold text-sm">IQ</span>
            </div>
            <div>
              <h4 className="text-white font-semibold">Energia IQ</h4>
              <p className="text-slate-400 text-xs">Intelligence Platform</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-[#1F3C88]/40 rounded-full">
              <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
              <span className="text-green-400 text-xs font-medium">Live Data</span>
            </div>
          </div>
        </div>
      </div>

      {/* Dashboard Content */}
      <div className="p-5 space-y-4">
        {/* Top Row - KPI Cards */}
        <div className="grid grid-cols-4 gap-3">
          <div className="bg-[#1F3C88]/40 rounded-lg p-4 border border-[#1F3C88]/30">
            <p className="text-slate-400 text-xs mb-1">Global Assets</p>
            <p className="text-white font-bold text-xl">12,847</p>
            <p className="text-green-400 text-xs mt-1">+2.4% MTD</p>
          </div>
          <div className="bg-[#1F3C88]/40 rounded-lg p-4 border border-[#1F3C88]/30">
            <p className="text-slate-400 text-xs mb-1">Countries</p>
            <p className="text-white font-bold text-xl">142</p>
            <p className="text-slate-400 text-xs mt-1">Full Coverage</p>
          </div>
          <div className="bg-[#1F3C88]/40 rounded-lg p-4 border border-[#1F3C88]/30">
            <p className="text-slate-400 text-xs mb-1">Data Points</p>
            <p className="text-white font-bold text-xl">2.8M</p>
            <p className="text-green-400 text-xs mt-1">Updated Daily</p>
          </div>
          <div className="bg-[#1F3C88]/40 rounded-lg p-4 border border-[#1F3C88]/30">
            <p className="text-slate-400 text-xs mb-1">Reports</p>
            <p className="text-white font-bold text-xl">1,250+</p>
            <p className="text-slate-400 text-xs mt-1">Available</p>
          </div>
        </div>

        {/* Middle Row - Charts */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-[#1F3C88]/30 rounded-lg p-4 border border-[#1F3C88]/20">
            <p className="text-slate-300 text-sm font-medium mb-3">Investment Flows ($B)</p>
            <div className="h-32">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={areaData}>
                  <defs>
                    <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#007BFF" stopOpacity={0.4}/>
                      <stop offset="95%" stopColor="#007BFF" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <Area type="monotone" dataKey="value" stroke="#007BFF" fill="url(#colorValue)" strokeWidth={2} />
                  <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="bg-[#1F3C88]/30 rounded-lg p-4 border border-[#1F3C88]/20">
            <p className="text-slate-300 text-sm font-medium mb-3">Capacity Additions (GW)</p>
            <div className="h-32">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barData}>
                  <Bar dataKey="value" fill="#10b981" radius={[4, 4, 0, 0]} />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Bottom Row - Mini Map */}
        <div className="bg-[#1F3C88]/30 rounded-lg p-4 border border-[#1F3C88]/20">
          <div className="flex items-center justify-between mb-3">
            <p className="text-slate-300 text-sm font-medium">Global Asset Distribution</p>
            <div className="flex gap-3 text-xs">
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#007BFF]"></span><span className="text-slate-400">Oil & Gas</span></span>
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#10b981]"></span><span className="text-slate-400">Power</span></span>
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#f59e0b]"></span><span className="text-slate-400">Mining</span></span>
            </div>
          </div>
          <div className="h-28 relative">
            <svg viewBox="-60 15 130 55" className="w-full h-full">
              <rect x="-60" y="15" width="130" height="55" fill="#0a1e38" rx="4" />
              <path d="M-50,28 Q-42,24 -32,27 L-28,35 Q-30,42 -38,40 L-48,35 Z" fill="#1F3C88" opacity="0.5" />
              <path d="M-32,48 Q-28,46 -25,52 L-30,62 Q-36,65 -38,58 Z" fill="#1F3C88" opacity="0.5" />
              <path d="M-5,32 Q8,30 15,35 L12,45 Q2,48 -8,42 Z" fill="#1F3C88" opacity="0.5" />
              <path d="M-2,46 Q8,44 15,50 L12,62 Q4,68 -4,60 Z" fill="#1F3C88" opacity="0.5" />
              <path d="M18,28 Q38,24 55,32 L58,45 Q48,52 30,48 L20,42 Z" fill="#1F3C88" opacity="0.5" />
              <path d="M48,54 Q56,52 62,56 L60,64 Q54,67 50,62 Z" fill="#1F3C88" opacity="0.5" />
              <circle cx="-38" cy="32" r="3" fill="#007BFF" opacity="0.9"><animate attributeName="r" values="3;4;3" dur="2s" repeatCount="indefinite" /></circle>
              <circle cx="12" cy="38" r="2.5" fill="#007BFF" opacity="0.8" />
              <circle cx="25" cy="35" r="3" fill="#007BFF" opacity="0.9"><animate attributeName="r" values="3;4;3" dur="2.5s" repeatCount="indefinite" /></circle>
              <circle cx="38" cy="32" r="2.5" fill="#10b981" opacity="0.8" />
              <circle cx="5" cy="52" r="2" fill="#f59e0b" opacity="0.8" />
              <circle cx="-35" cy="36" r="2" fill="#10b981" opacity="0.8" />
              <circle cx="45" cy="38" r="2.5" fill="#10b981" opacity="0.9"><animate attributeName="r" values="2.5;3.5;2.5" dur="3s" repeatCount="indefinite" /></circle>
              <circle cx="52" cy="58" r="2" fill="#f59e0b" opacity="0.8" />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

// Intelligence Platforms (same as Products page)
const intelligencePlatforms = [
  {
    name: 'Commodity Prices',
    description: 'Real-time price intelligence across major energy and mineral commodities with historical trends and forecasting models.',
    image: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=800&q=80',
    coverage: ['Crude Oil', 'Natural Gas', 'Coal', 'Critical Minerals', 'Carbon Credits']
  },
  {
    name: 'Crude Oil',
    description: 'Comprehensive crude oil market analysis covering global supply-demand dynamics, refinery operations, and trade flows.',
    image: 'https://images.unsplash.com/photo-1570065843263-db1cc228c6e4?w=800&q=80',
    coverage: ['Prices', 'Reserves', 'Production', 'Consumption', 'Trade Movements', 'Refinery Capacity and Production']
  },
  {
    name: 'Natural Gas',
    description: 'In-depth natural gas intelligence including LNG markets, pipeline infrastructure, and emerging hydrogen production.',
    image: 'https://images.unsplash.com/photo-1761848021920-ff2a212671be?w=800&q=80',
    coverage: ['Prices', 'Reserves', 'Production', 'Consumption', 'Hydrogen Production Capacity', 'Trade Movements', 'Liquefaction Capacity and Production']
  },
  {
    name: 'Coal',
    description: 'Strategic coal market analysis covering thermal and metallurgical coal across major producing and consuming regions.',
    image: 'https://images.unsplash.com/photo-1632734825563-e180215f0c90?w=800&q=80',
    coverage: ['Prices', 'Reserves', 'Production', 'Consumption', 'Trade Movements']
  },
  {
    name: 'Solar Energy',
    description: 'Comprehensive solar energy intelligence tracking photovoltaic installations, capacity growth, and technology developments.',
    image: 'https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?w=800&q=80',
    coverage: ['Installed Capacity', 'Installation by Type']
  },
  {
    name: 'Wind Energy',
    description: 'Wind power market analysis covering onshore and offshore installations, turbine technologies, and capacity expansion.',
    image: 'https://images.unsplash.com/photo-1591511017724-d2e18d65bf5d?w=800&q=80',
    coverage: ['Installed Capacity', 'Installation by Type']
  },
  {
    name: 'Geothermal Energy',
    description: 'Geothermal power analysis tracking steam field developments, plant operations, and capacity expansion globally.',
    image: 'https://images.unsplash.com/photo-1759934553802-749f348a50b3?w=800&q=80',
    coverage: ['Production']
  },
  {
    name: 'Nuclear Energy',
    description: 'Nuclear power market analysis covering reactor operations, uranium markets, and global capacity planning.',
    image: 'https://images.unsplash.com/photo-1700607101915-99650c815caf?w=800&q=80',
    coverage: ['Production']
  },
  {
    name: 'Bio Energy',
    description: 'Bioenergy sector intelligence covering biomass, biofuels, and organic feedstock markets across global regions.',
    image: 'https://images.unsplash.com/photo-1759302081336-f79d96ecf83f?w=800&q=80',
    coverage: ['Production', 'Consumption']
  },
  {
    name: 'Hydropower',
    description: 'Hydroelectric power intelligence covering dam operations, water resource management, and capacity developments.',
    image: 'https://images.unsplash.com/photo-1575041057420-505200c5f8d0?w=800&q=80',
    coverage: ['Production']
  },
  {
    name: 'Critical Minerals',
    description: 'Strategic intelligence on lithium, cobalt, rare earth elements, and other critical minerals essential for energy transition.',
    image: 'https://images.unsplash.com/photo-1634588513592-e34817f2e9e5?w=800&q=80',
    coverage: ['Prices', 'Reserves', 'Production', 'Consumption', 'Trade Movements']
  },
  {
    name: 'Emissions Tracking',
    description: 'Comprehensive emissions monitoring and analysis covering greenhouse gas data, carbon markets, and climate policy impacts.',
    image: 'https://images.unsplash.com/photo-1769445205155-5158d692d3ed?w=800&q=80',
    coverage: ['Global GHG Emissions', 'Sectoral Emissions', 'Historical Emissions', 'Forward Emissions', 'Regional Emissions']
  }
];

// Decision Intelligence Workflows
const workflows = [
  {
    icon: <Search className="w-8 h-8 text-[#007BFF]" />,
    title: 'Asset Discovery',
    description: 'Identify and analyse global energy assets including oil fields, LNG facilities, power plants, and mining projects.'
  },
  {
    icon: <Building2 className="w-8 h-8 text-[#007BFF]" />,
    title: 'Infrastructure Intelligence',
    description: 'Analyse pipelines, LNG terminals, refineries, electricity networks, and energy transport systems.'
  },
  {
    icon: <TrendingUp className="w-8 h-8 text-[#007BFF]" />,
    title: 'Market Forecasting',
    description: 'Track supply, demand, pricing dynamics, and long-term market outlook across global energy commodities.'
  },
  {
    icon: <PiggyBank className="w-8 h-8 text-[#007BFF]" />,
    title: 'Investment Screening',
    description: 'Evaluate project opportunities, energy infrastructure investments, mergers and acquisitions, and capital flows.'
  },
  {
    icon: <Leaf className="w-8 h-8 text-[#007BFF]" />,
    title: 'Energy Transition Analysis',
    description: 'Assess decarbonisation pathways, emissions trends, renewable energy growth, and climate policy impacts.'
  },
  {
    icon: <LineChart className="w-8 h-8 text-[#007BFF]" />,
    title: 'Scenario Modelling',
    description: 'Explore alternative energy transition scenarios and evaluate the long-term evolution of global energy systems.'
  }
];

// Platform Capabilities
const capabilities = [
  {
    icon: <Database className="w-10 h-10 text-[#007BFF]" />,
    title: 'Proprietary Data Intelligence',
    description: 'Structured datasets covering global energy assets, infrastructure systems, markets, and investments.'
  },
  {
    icon: <Globe2 className="w-10 h-10 text-[#007BFF]" />,
    title: 'Market Intelligence',
    description: 'Data-driven insights into supply, demand, trade flows, and commodity market dynamics.'
  },
  {
    icon: <Coins className="w-10 h-10 text-[#007BFF]" />,
    title: 'Investment Intelligence',
    description: 'Analytics supporting investment decisions across energy assets, projects, and infrastructure systems.'
  },
  {
    icon: <Sprout className="w-10 h-10 text-[#007BFF]" />,
    title: 'Energy Transition Intelligence',
    description: 'Insights on emissions trends, decarbonisation strategies, and global climate policy developments.'
  }
];

// Data Visualization Components
const LNGMapVisualization = () => (
  <div className="relative w-full h-52 bg-[#f8fafc] rounded-lg overflow-hidden">
    <svg viewBox="-60 10 130 70" className="w-full h-full">
      <defs>
        <linearGradient id="oceanGrad2" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#e0f2fe" />
          <stop offset="100%" stopColor="#bae6fd" />
        </linearGradient>
      </defs>
      <rect x="-60" y="10" width="130" height="70" fill="url(#oceanGrad2)" />
      <path d="M-50,25 Q-45,20 -35,22 L-30,28 Q-25,35 -30,45 L-45,42 Q-55,35 -50,25" fill="#e2e8f0" stroke="#94a3b8" strokeWidth="0.3" />
      <path d="M-5,30 Q5,28 15,32 L12,45 Q5,48 -5,45 L-8,38 Q-8,32 -5,30" fill="#e2e8f0" stroke="#94a3b8" strokeWidth="0.3" />
      <path d="M15,25 Q35,20 55,28 L60,45 Q50,55 35,50 L20,45 Q15,38 15,25" fill="#e2e8f0" stroke="#94a3b8" strokeWidth="0.3" />
      <path d="M45,58 Q55,55 62,60 L60,70 Q52,73 48,68 L45,62 Q44,59 45,58" fill="#e2e8f0" stroke="#94a3b8" strokeWidth="0.3" />
      <circle cx="15" cy="35" r="3" fill="#007BFF" opacity="0.9" />
      <circle cx="52" cy="32" r="3" fill="#007BFF" opacity="0.9" />
      <circle cx="8" cy="28" r="2.5" fill="#007BFF" opacity="0.8" />
      <circle cx="-35" cy="42" r="2.5" fill="#10b981" opacity="0.8" />
      <circle cx="45" cy="40" r="2.5" fill="#10b981" opacity="0.8" />
      <circle cx="0" cy="40" r="2" fill="#10b981" opacity="0.7" />
      <circle cx="38" cy="35" r="2" fill="#007BFF" opacity="0.7" />
    </svg>
    <div className="absolute bottom-3 left-3 flex gap-4 text-xs">
      <span className="flex items-center gap-1.5 bg-white/80 px-2 py-1 rounded"><span className="w-2 h-2 rounded-full bg-[#007BFF]"></span>Liquefaction</span>
      <span className="flex items-center gap-1.5 bg-white/80 px-2 py-1 rounded"><span className="w-2 h-2 rounded-full bg-[#10b981]"></span>Regasification</span>
    </div>
  </div>
);

const RefineryChartVisualization = () => {
  const data = [
    { region: 'Asia Pacific', capacity: 35.2 },
    { region: 'North America', capacity: 21.8 },
    { region: 'Europe', capacity: 14.5 },
    { region: 'Middle East', capacity: 11.2 },
    { region: 'Africa', capacity: 3.8 },
  ];
  return (
    <div className="w-full h-52 bg-[#f8fafc] rounded-lg p-4">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ top: 5, right: 20, left: 70, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={true} vertical={false} />
          <XAxis type="number" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} tickFormatter={(value) => `${value}M`} />
          <YAxis type="category" dataKey="region" tick={{ fontSize: 10, fill: '#334155' }} axisLine={false} tickLine={false} width={65} />
          <Bar dataKey="capacity" fill="#007BFF" radius={[0, 4, 4, 0]} barSize={18} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const PipelineNetworkVisualization = () => (
  <div className="relative w-full h-52 bg-[#f8fafc] rounded-lg overflow-hidden p-3">
    <svg viewBox="0 0 300 160" className="w-full h-full">
      <defs>
        <pattern id="gridPattern2" width="15" height="15" patternUnits="userSpaceOnUse">
          <path d="M 15 0 L 0 0 0 15" fill="none" stroke="#e2e8f0" strokeWidth="0.5"/>
        </pattern>
      </defs>
      <rect width="300" height="160" fill="url(#gridPattern2)" />
      <path d="M220,35 Q180,45 140,55 Q100,65 70,70" stroke="#007BFF" strokeWidth="4" fill="none" strokeLinecap="round" opacity="0.7"/>
      <path d="M150,85 Q190,75 240,65" stroke="#007BFF" strokeWidth="3" fill="none" strokeLinecap="round" opacity="0.6"/>
      <path d="M100,120 Q90,95 80,75" stroke="#1F3C88" strokeWidth="3" fill="none" strokeLinecap="round" opacity="0.6"/>
      <circle cx="220" cy="35" r="7" fill="#007BFF" opacity="0.9"/>
      <text x="220" y="22" textAnchor="middle" fontSize="9" fill="#334155">Russia</text>
      <circle cx="70" cy="70" r="6" fill="#1F3C88" opacity="0.9"/>
      <text x="70" y="58" textAnchor="middle" fontSize="9" fill="#334155">Europe</text>
      <circle cx="150" cy="85" r="7" fill="#007BFF" opacity="0.9"/>
      <text x="150" y="102" textAnchor="middle" fontSize="9" fill="#334155">Middle East</text>
      <circle cx="240" cy="65" r="6" fill="#1F3C88" opacity="0.9"/>
      <text x="240" y="53" textAnchor="middle" fontSize="9" fill="#334155">Asia</text>
    </svg>
    <div className="absolute bottom-3 left-3 flex gap-4 text-xs">
      <span className="flex items-center gap-1.5 bg-white/80 px-2 py-1 rounded"><span className="w-5 h-0.5 bg-[#007BFF] rounded"></span>Gas</span>
      <span className="flex items-center gap-1.5 bg-white/80 px-2 py-1 rounded"><span className="w-5 h-0.5 bg-[#1F3C88] rounded"></span>Oil</span>
    </div>
  </div>
);

const InvestmentHeatmapVisualization = () => {
  const data = [
    { year: '2021', value: 320 },
    { year: '2022', value: 385 },
    { year: '2023', value: 445 },
    { year: '2024', value: 520 },
    { year: '2025', value: 580 },
  ];
  return (
    <div className="w-full h-52 bg-[#f8fafc] rounded-lg p-4">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
          <XAxis dataKey="year" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} />
          <YAxis tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} tickFormatter={(value) => `$${value}B`} />
          <Tooltip formatter={(value) => [`$${value}B`, 'Investment']} contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '11px' }} />
          <Area type="monotone" dataKey="value" stroke="#007BFF" fill="#007BFF" fillOpacity={0.2} strokeWidth={2} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

const visualizations = [
  { title: 'Global LNG Infrastructure', caption: 'Liquefaction and regasification projects worldwide', component: <LNGMapVisualization /> },
  { title: 'Refinery Capacity by Region', caption: 'Comparative refining capacity across major hubs', component: <RefineryChartVisualization /> },
  { title: 'Pipeline Network Intelligence', caption: 'Strategic oil and gas pipeline corridors', component: <PipelineNetworkVisualization /> },
  { title: 'Energy Investment Trends', caption: 'Global capital deployment in energy projects', component: <InvestmentHeatmapVisualization /> }
];

// Main Page Component
const EnergiaIQPage = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#1F7A4F] via-[#2C6E73] to-[#284A86] text-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 whitespace-nowrap">Energia IQ</h1>
            <p className="text-base lg:text-lg text-white/90 leading-relaxed">
              The integrated intelligence platform delivering proprietary datasets, analytical models, and market intelligence across global energy systems.
            </p>
          </div>
        </div>
      </section>

      {/* Intelligence Platforms Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl lg:text-3xl font-bold text-[#0F2A4D] mb-4">
              Our Intelligence Platforms
            </h2>
            <p className="text-base lg:text-lg text-slate-600 max-w-3xl mx-auto">
              Comprehensive intelligence coverage across global energy commodities and markets
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {intelligencePlatforms.map((platform, index) => (
              <div
                key={index}
                className="bg-slate-50 rounded-xl border border-slate-200 hover:border-[#007BFF] hover:shadow-xl transition-all duration-300 overflow-hidden group flex flex-col"
              >
                {/* Image Section with White Title */}
                <div className="relative h-48 overflow-hidden flex-shrink-0">
                  <img
                    src={platform.image}
                    alt={platform.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0F2A4D]/80 to-transparent"></div>
                  <h3 className="absolute bottom-4 left-4 text-2xl font-bold text-white">
                    {platform.name}
                  </h3>
                </div>

                {/* Description */}
                <div className="p-6 border-b border-slate-200 bg-white">
                  <p className="text-slate-600 leading-relaxed text-sm">
                    {platform.description}
                  </p>
                </div>

                {/* Coverage Areas */}
                <div className="p-6 bg-white flex-grow">
                  <div className="text-sm font-semibold text-[#0F2A4D] mb-3">
                    Coverage Areas:
                  </div>
                  <ul className="space-y-2">
                    {platform.coverage.map((item, idx) => (
                      <li key={idx} className="flex items-center space-x-2 text-sm text-slate-600">
                        <div className="w-1.5 h-1.5 bg-[#007BFF] rounded-full flex-shrink-0"></div>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Button */}
                <div className="px-6 pb-6 bg-white mt-auto">
                  <Link
                    to="/contact"
                    className="flex items-center justify-center w-full py-3 bg-[#8B0000] text-white font-medium rounded-md hover:bg-[#6B0000] transition-all duration-200 group/btn"
                  >
                    Request Access
                    <ArrowRight className="ml-2 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Decision Intelligence Workflows Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl lg:text-3xl font-bold text-[#0F2A4D] mb-4">
              Energia IQ Decision Intelligence Workflows
            </h2>
            <p className="text-base lg:text-lg text-slate-600 max-w-3xl mx-auto">
              Structured workflows supporting strategic decision-making across energy markets
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {workflows.map((workflow, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-xl border border-slate-200 hover:border-[#007BFF] hover:shadow-lg transition-all duration-300 group"
              >
                <div className="mb-4 p-3 bg-slate-50 rounded-lg inline-block group-hover:bg-[#007BFF]/10 transition-colors duration-300">
                  {workflow.icon}
                </div>
                <h3 className="text-lg font-semibold text-[#0F2A4D] mb-2 group-hover:text-[#007BFF] transition-colors">
                  {workflow.title}
                </h3>
                <p className="text-base text-slate-600 leading-relaxed">
                  {workflow.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Capabilities Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl lg:text-3xl font-bold text-[#0F2A4D] mb-4">
              Energia IQ Platform Capabilities
            </h2>
            <p className="text-base lg:text-lg text-slate-600 max-w-3xl mx-auto">
              Core intelligence capabilities powering strategic energy decisions
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {capabilities.map((capability, index) => (
              <div
                key={index}
                className="bg-slate-50 p-6 rounded-xl border border-slate-200 hover:border-[#007BFF] hover:shadow-lg transition-all duration-300 group text-center"
              >
                <div className="mb-4 p-3 bg-white rounded-lg inline-block shadow-sm group-hover:shadow-md group-hover:bg-[#007BFF]/5 transition-all duration-300">
                  {capability.icon}
                </div>
                <h3 className="text-lg font-semibold text-[#0F2A4D] mb-2 group-hover:text-[#007BFF] transition-colors">
                  {capability.title}
                </h3>
                <p className="text-base text-slate-600 leading-relaxed">
                  {capability.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Data Intelligence Visualizations Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl lg:text-3xl font-bold text-[#0F2A4D] mb-4">
              Energia IQ Data Intelligence
            </h2>
            <p className="text-base lg:text-lg text-slate-600 max-w-3xl mx-auto">
              Analytical outputs demonstrating the intelligence capabilities of the Energia IQ platform
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {visualizations.map((viz, index) => (
              <div
                key={index}
                className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg hover:border-[#007BFF]/50 transition-all duration-300 group"
              >
                <div className="p-4">
                  {viz.component}
                </div>
                <div className="px-6 pb-6">
                  <h3 className="text-lg font-bold text-[#0F2A4D] mb-2 group-hover:text-[#007BFF] transition-colors">
                    {viz.title}
                  </h3>
                  <p className="text-sm text-slate-600">
                    {viz.caption}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#157D3F] via-[#195C63] to-[#1D3B87]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4">
            Access the Energia IQ Platform
          </h2>
          <p className="text-base lg:text-lg text-blue-100 mb-10 leading-relaxed">
            Join leading energy companies, investors, and policymakers leveraging Energia IQ for strategic decision-making across global energy markets.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 bg-[#007BFF] text-white font-semibold rounded-md hover:bg-[#0066DD] transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              Explore Energia IQ
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-[#0F2A4D] font-semibold rounded-md hover:bg-slate-100 transition-all duration-200 shadow-lg"
            >
              Request Platform Access
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EnergiaIQPage;
